/**
 * WebGL2 renderer for one HU slice. The frame is uploaded as an R32F float
 * texture holding true Hounsfield units; window/level is applied in the shader
 * every draw, so W/L is a cheap uniform update at full precision — real HU
 * windowing, not a filter on baked 8-bit. LINEAR filtering upscales smoothly.
 *
 * Context-loss handling: the last frame and window are retained, and
 * `webglcontextrestored` rebuilds the program/texture and redraws. This
 * covers two real cases:
 *  - a GPU crash / context eviction mid-session (otherwise the pane becomes
 *    Chrome's white "sad canvas" placeholder until a full page reload), and
 *  - construction on an already-lost context: dispose() force-loses the
 *    context to stay under the per-page context budget, but React StrictMode's
 *    dev double-mount reuses the same canvas, whose getContext() then returns
 *    that dead context. The constructor requests a restore and defers all GPU
 *    setup to the restored handler.
 */
const VERT = `#version 300 es
precision highp float;
out vec2 vUV;
void main() {
  vec2 p = vec2((gl_VertexID << 1) & 2, gl_VertexID & 2);
  vUV = vec2(p.x, 1.0 - p.y);
  gl_Position = vec4(p * 2.0 - 1.0, 0.0, 1.0);
}`;

const FRAG = `#version 300 es
precision highp float;
uniform sampler2D uTex;
uniform float uLo;    // window low edge, HU
uniform float uInvW;  // 1 / window width
uniform float uGamma; // display gamma (film characteristic curve); 1.0 = linear
in vec2 vUV;
out vec4 frag;
void main() {
  float hu = texture(uTex, vUV).r;
  float g = clamp((hu - uLo) * uInvW, 0.0, 1.0);
  if (uGamma != 1.0) g = pow(g, uGamma);
  frag = vec4(g, g, g, 1.0);
}`;

// WEBGL_lose_context is unobtainable from an already-lost context (getExtension
// returns null), so it is captured per context while alive. A renderer reusing
// a canvas whose context was force-lost by its predecessor's dispose() finds
// the handle here to request restoration — verified: without this, the reused
// pane can never recover.
const loseExtByContext = new WeakMap<WebGL2RenderingContext, WEBGL_lose_context>();

function loseExtFor(gl: WebGL2RenderingContext): WEBGL_lose_context | null {
    let ext = loseExtByContext.get(gl) ?? null;
    if (!ext && !gl.isContextLost()) {
        ext = gl.getExtension('WEBGL_lose_context');
        if (ext) loseExtByContext.set(gl, ext);
    }
    return ext;
}

function compile(gl: WebGL2RenderingContext, type: number, src: string): WebGLShader {
    const sh = gl.createShader(type)!;
    gl.shaderSource(sh, src);
    gl.compileShader(sh);
    if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
        throw new Error(`shader: ${gl.getShaderInfoLog(sh)}`);
    }
    return sh;
}

export class SliceRenderer {
    private canvas: HTMLCanvasElement;
    private gl: WebGL2RenderingContext;
    private prog: WebGLProgram | null = null;
    private tex: WebGLTexture | null = null;
    private uLo: WebGLUniformLocation | null = null;
    private uInvW: WebGLUniformLocation | null = null;
    private uGamma: WebGLUniformLocation | null = null;
    /** GPU resources exist and the context is usable. */
    private ready = false;
    private hasTex = false;
    private disposed = false;
    /** A restore we asked for ourselves (canvas reuse) — expected, not an error. */
    private expectRestore = false;
    // Retained so a (re)gained context can rebuild the exact previous state.
    private lastFrame: { data: Float32Array; w: number; h: number } | null = null;
    private lastWin: { center: number; width: number; gamma: number } | null = null;

    constructor(canvas: HTMLCanvasElement) {
        this.canvas = canvas;
        const gl = canvas.getContext('webgl2', { antialias: false, alpha: false });
        if (!gl) throw new Error('WebGL2 unavailable');
        this.gl = gl;
        canvas.addEventListener('webglcontextlost', this.onContextLost);
        canvas.addEventListener('webglcontextrestored', this.onContextRestored);
        if (gl.isContextLost()) {
            // The canvas's previous renderer force-lost this context on dispose and
            // the canvas is being reused (React StrictMode's dev double-mount does
            // exactly this). restoreContext() is only honored AFTER the
            // webglcontextlost event has been dispatched and preventDefault'ed, so
            // the request lives in onContextLost; GPU setup then happens in
            // onContextRestored. Best-effort here too, in case the lost event
            // already passed.
            this.expectRestore = true;
            this.requestRestore();
            return;
        }
        loseExtFor(gl); // capture the extension while the context is alive
        this.initResources();
    }

    // preventDefault is REQUIRED: without it the browser considers the context
    // unrecoverable and never fires webglcontextrestored.
    private onContextLost = (e: Event): void => {
        e.preventDefault();
        this.ready = false;
        this.hasTex = false;
        if (this.disposed) return;
        if (this.expectRestore) {
            // Our own dispose-induced loss on a reused canvas. Restoration is only
            // honored once this event's default is prevented (just done above), so
            // request it now. Expected — stay quiet.
            this.requestRestore();
            return;
        }
        console.warn(
            'CBCT slice renderer: WebGL context lost (GPU crash or context eviction) — waiting for restore'
        );
    };

    /** Ask the browser to restore a context force-lost via WEBGL_lose_context.
     *  Runs from a fresh task, per the Khronos guidance that a restoreContext()
     *  inside the lost handler itself may be ignored. A no-op when the loss
     *  wasn't extension-induced (real GPU loss: the browser decides). */
    private requestRestore(): void {
        const ext = loseExtByContext.get(this.gl);
        if (!ext) return;
        setTimeout(() => {
            try {
                ext.restoreContext();
            } catch {
                /* already restored or restore in flight */
            }
        }, 0);
    }

    private onContextRestored = (): void => {
        const wasExpected = this.expectRestore;
        this.expectRestore = false;
        try {
            this.initResources();
        } catch (err) {
            // GPU still unavailable; a later restore event may succeed.
            console.warn('CBCT slice renderer: context restore failed:', err);
            return;
        }
        this.gl.viewport(0, 0, this.canvas.width, this.canvas.height);
        const frame = this.lastFrame;
        const win = this.lastWin;
        if (frame) this.setFrame(frame.data, frame.w, frame.h);
        if (win) this.draw(win.center, win.width, win.gamma);
        if (!wasExpected) {
            console.warn('CBCT slice renderer: WebGL context restored, frame redrawn');
        }
    };

    /** (Re)creates all GL resources on the already-acquired context. */
    private initResources(): void {
        const gl = this.gl;
        if (gl.isContextLost()) throw new Error('WebGL context is lost');
        this.ready = false;
        this.hasTex = false;
        const linear = gl.getExtension('OES_texture_float_linear') !== null;

        const prog = gl.createProgram()!;
        const vs = compile(gl, gl.VERTEX_SHADER, VERT);
        const fs = compile(gl, gl.FRAGMENT_SHADER, FRAG);
        gl.attachShader(prog, vs);
        gl.attachShader(prog, fs);
        gl.linkProgram(prog);
        // Once linked, the shader objects are no longer needed; flag them for
        // deletion so the driver frees them when the program is disposed.
        gl.deleteShader(vs);
        gl.deleteShader(fs);
        if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
            throw new Error(`link: ${gl.getProgramInfoLog(prog)}`);
        }
        this.prog = prog;
        gl.useProgram(prog);
        this.uLo = gl.getUniformLocation(prog, 'uLo');
        this.uInvW = gl.getUniformLocation(prog, 'uInvW');
        this.uGamma = gl.getUniformLocation(prog, 'uGamma');
        gl.uniform1i(gl.getUniformLocation(prog, 'uTex'), 0);

        const tex = gl.createTexture()!;
        this.tex = tex;
        const filter = linear ? gl.LINEAR : gl.NEAREST;
        gl.bindTexture(gl.TEXTURE_2D, tex);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, filter);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, filter);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        this.ready = true;
    }

    setFrame(data: Float32Array, w: number, h: number): void {
        // Always retain: if the context isn't usable yet (pending restore), the
        // restored handler replays this frame.
        this.lastFrame = { data, w, h };
        if (!this.ready) return;
        const gl = this.gl;
        gl.bindTexture(gl.TEXTURE_2D, this.tex);
        gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.R32F, w, h, 0, gl.RED, gl.FLOAT, data);
        this.hasTex = true;
    }

    resize(w: number, h: number): void {
        const c = this.canvas;
        if (c.width !== w || c.height !== h) {
            c.width = w;
            c.height = h;
            if (this.ready) this.gl.viewport(0, 0, w, h);
        }
    }

    draw(center: number, width: number, gamma = 1.0): void {
        // Retain the window even when not drawable yet, for the restore replay.
        this.lastWin = { center, width, gamma };
        if (!this.ready || !this.hasTex) return;
        const gl = this.gl;
        gl.useProgram(this.prog);
        gl.uniform1f(this.uLo, center - width / 2);
        gl.uniform1f(this.uInvW, 1 / Math.max(width, 1e-6));
        gl.uniform1f(this.uGamma, gamma);
        gl.drawArrays(gl.TRIANGLES, 0, 3);
    }

    dispose(): void {
        this.disposed = true;
        this.canvas.removeEventListener('webglcontextlost', this.onContextLost);
        this.canvas.removeEventListener('webglcontextrestored', this.onContextRestored);
        this.lastFrame = null;
        this.lastWin = null;
        const gl = this.gl;
        if (this.ready) {
            gl.deleteTexture(this.tex);
            gl.deleteProgram(this.prog);
        }
        this.ready = false;
        // Release the underlying GL context so the browser can reclaim it; without
        // this each disposed renderer leaks a context and hits the per-page limit.
        // A renderer constructed later on the SAME canvas detects the lost context
        // and restores it via the extension cached in loseExtByContext.
        if (!gl.isContextLost()) {
            loseExtFor(gl)?.loseContext();
        }
    }
}
